import { ArrowDropDown } from '@mui/icons-material';
import type { MenuResponse } from '@on3/api';
import { Link } from '@on3/ui-lib/components/Link';
import { ServiceLogo } from '@on3/ui-lib/components/ServiceLogo/ServiceLogo';
import { Tag } from '@on3/ui-lib/components/Tag';
import { parseMenuTitle } from '@on3/ui-lib/utils/parsers';
import { HeaderThemes, SiteThemes } from '@on3/ui-lib/utils/sites';
import { useHeader } from '@public/components/Header';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';

import styles from './Menu.module.scss';

interface MenuProps {
  data: MenuResponse[] | null;
  isSecondary?: boolean;
  className?: string;
}

export const Menu = ({ data, isSecondary = false, className }: MenuProps) => {
  const {
    themes: {
      isTeam,
      isChannel,
      isRecruits,
      header: headerTheme,
      site: siteTheme,
    },
  } = useHeader();
  const pathname = usePathname();

  const isActive = (item: MenuResponse): boolean => {
    if ([item.url, `${item.url}/`].includes(pathname)) return true;

    if (item.children && item.children.length > 0) {
      return item.children.some((child) => isActive(child));
    }

    return false;
  };

  if (!data) return null;

  const showSiteLogo =
    (headerTheme === HeaderThemes.PRIMARY && isSecondary) ||
    (headerTheme === HeaderThemes.SECONDARY &&
      !isSecondary &&
      !isTeam &&
      (!isChannel || isRecruits));
  const siteLogoType = siteTheme === SiteThemes.ON3 ? 'rivals' : 'on3';

  const SiteLogo = () =>
    siteLogoType === 'on3' ? (
      <Link href="/">
        <ServiceLogo data-logo="on3" type="on3" />
      </Link>
    ) : (
      <Link href="/rivals/">
        <ServiceLogo data-logo="rivals" type="rivals" />
      </Link>
    );

  return (
    <nav
      className={clsx(styles.block, className, {
        [styles.isSecondary]: isSecondary,
      })}
      data-menu-ui="menu"
    >
      <ul className={styles.menu}>
        {data?.map((item: MenuResponse, key) => {
          const { title, tag } = parseMenuTitle(item.title);

          return (
            <li
              className={styles.menuItem}
              key={`${item.id}-${item.key}-${key}`}
            >
              <a
                aria-haspopup={item?.children?.length ? 'true' : 'false'}
                data-active={isActive(item)}
                data-has-tag={!!tag}
                data-parent
                href={item?.url || '/'}
              >
                {tag && <Tag type={tag} />}
                {title}
                {item?.children?.length && (
                  <ArrowDropDown className={styles.arrow} fill="currentColor" />
                )}
              </a>
              {item?.children?.length && (
                <ul className={styles.dropdown} data-menu-ui="dropdown">
                  {item?.children.map((subItem, index) => (
                    <li
                      className={styles.dropdownItem}
                      key={`${subItem.id}-${subItem.key}-${index}`}
                    >
                      <a
                        data-active={isActive(subItem)}
                        href={subItem?.url || '/'}
                      >
                        {subItem.title}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      {showSiteLogo && (
        <div className={clsx(styles.menuItem, styles.siteLogo)}>
          <SiteLogo />
        </div>
      )}
    </nav>
  );
};
